import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import {
  AccountTransaction,
  HorizontalListData,
  HorizontalListFactory,
  LoanWithRenewalData,
  SimpleHorizontalListAppearanceType,
  SimpleHorizontalListBgColorType,
  SimpleHorizontalListWidgetConfigData,
  TransactionType,
} from 'common';

@Injectable()
export class AccountTransactionDetailAdapter {
  constructor(private readonly datePipe: DatePipe) {}

  adaptListData = (transaction: AccountTransaction, loan: LoanWithRenewalData): HorizontalListData[] => {
    return [
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Transfer from',
        displayedValue: this.getTransferFrom(transaction, loan),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Transfer to',
        displayedValue: this.getTransferTo(transaction, loan),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Initiator',
        displayedValue: transaction?.initiator,
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Modified by',
        displayedValue: transaction?.modifier,
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Transaction date',
        displayedValue: this.formatDate(transaction?.date),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Processed date',
        displayedValue: this.formatDate(transaction?.processDate),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Complete date',
        displayedValue: this.formatDate(transaction?.completeDate),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'ID',
        displayedValue: transaction?.id?.toString(),
      }),
      HorizontalListFactory.createKeyValueRouterLinkItem({
        title: 'Related transaction',
        displayedValue: transaction?.relatedTransactionId?.toString(),
        routerLink: `/account/${loan.id}/transaction/${transaction?.relatedTransactionId}`,
        isItemHidden: !transaction?.relatedTransactionId,
      }),
    ];
  };

  adaptWidgetConfigData = (): SimpleHorizontalListWidgetConfigData => {
    return {
      backgroundColorType: SimpleHorizontalListBgColorType.Grey,
      cardAppearanceType: SimpleHorizontalListAppearanceType.Outlined,
    };
  };

  private formatDate(value: Date): string {
    return this.datePipe.transform(value, 'short') || '-';
  }

  private getTransferFrom(transaction: AccountTransaction, loan: LoanWithRenewalData): string {
    if (!loan || !loan.loanInfo) return undefined;

    if (transaction?.type === TransactionType.Draw || transaction?.type === TransactionType.Premium)
      return `${transaction?.loanProductName}: ${transaction?.loanNumber}`;
    else if (transaction?.type === TransactionType.BalanceTransfer) {
      if (transaction.amount > 0) return `${loan.renewalForProductName}: ${loan.renewalForLoanNumber}`;
      else return `${transaction.loanProductName}: ${transaction.loanNumber}`;
    } else
      return loan.loanInfo.disbursementBankName && loan.loanInfo.disbursementBankAccount
        ? `${loan.loanInfo.disbursementBankName} : ${loan.loanInfo.disbursementBankAccount}`
        : 'Your bank account on file';
  }

  private getTransferTo(transaction: AccountTransaction, loan: LoanWithRenewalData): string {
    if (!loan || !loan.loanInfo) return undefined;

    if (transaction?.type === TransactionType.Draw)
      return loan.loanInfo.disbursementBankName && loan.loanInfo.disbursementBankAccount
        ? `${loan.loanInfo.disbursementBankName} : ${loan.loanInfo.disbursementBankAccount}`
        : 'Your bank account on file';
    else if (transaction?.type === TransactionType.BalanceTransfer) {
      if (transaction.amount > 0) return `${loan.productName}: ${loan.loanNumber}`;
      else
        return loan.renewedBy
          ? `${loan.renewedByProductName}: ${loan.renewedByLoanNumber}`
          : `${loan.renewalForProductName}: ${loan.renewalForLoanNumber}`;
    } else if (transaction?.type === TransactionType.Premium) return 'External account';
    else return `${transaction?.loanProductName}: ${transaction?.loanNumber}`;
  }
}
